import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Pencil, Sparkles, Undo2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

export const STATUTS_IA = { propose: "À valider", fait: "Fait", refuse: "Refusé", annule: "Annulé", erreur: "Échec" };
export const signalerMaj = () => window.dispatchEvent(new Event("assistant:maj"));

const versLocal = (iso) => (iso ? iso.slice(0, 16) : "");

const ChampAction = ({ c, valeur, onChange, testId }) => {
  if (c.type === "liste") return <ChoixListe valeur={valeur} onChange={onChange} options={c.options} testId={testId} />;
  if (c.type === "datetime") return <input type="datetime-local" className="at-input" value={versLocal(valeur)} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  const v = c.type === "jours" && Array.isArray(valeur) ? valeur.join(", ") : valeur ?? "";
  return <input className="at-input" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
};

export const CarteAction = ({ action: initiale, onFait }) => {
  const [action, setAction] = useState(initiale);
  useEffect(() => setAction(initiale), [initiale]);
  const [edition, setEdition] = useState(false);
  const [params, setParams] = useState(action.params);
  const [envoi, setEnvoi] = useState(false);
  const formRef = useRef(null);
  useEffect(() => { if (edition) formRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [edition]);
  const valider = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.post(`/assistant/actions/${action.id}/valider`, { params });
      if (data.statut === "fait") toast.success("C'est fait.", { description: data.resume });
      else toast.error(data.erreur || "Action impossible.");
      setAction({ ...action, ...data });
      signalerMaj();
      onFait?.(data);
    } catch (e) { toast.error(messageErreur(e)); } finally { setEnvoi(false); }
  };
  const annuler = async () => {
    try { await api.post(`/assistant/actions/${action.id}/annuler`); setAction({ ...action, statut: "annule" }); toast.success("Action annulée."); signalerMaj(); }
    catch (e) { toast.error(messageErreur(e)); }
  };
  const traitee = action.statut !== "propose";
  return (
    <div className="at-soft p-4 space-y-3 min-w-0" data-testid={`ai-action-card-${action.id}`}>
      <div className="flex items-start gap-2 min-w-0">
        <Sparkles size={16} className="shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-xs at-label">{action.outil_label} · {STATUTS_IA[action.statut]}</p>
          <p className="font-semibold break-words" data-testid="ai-action-summary">{action.resume}</p>
          {action.fiche_id && <Link to={`/fiches/${action.fiche_id}`} className="text-xs font-semibold underline">{action.fiche_nom}</Link>}
          {action.erreur && <p className="text-sm mt-1" style={{ color: "var(--danger)" }} data-testid="ai-action-error">{action.erreur}</p>}
        </div>
      </div>
      {action.apres && (
        <div className="grid grid-cols-1 gap-2 text-xs" data-testid="ai-action-diff">
          <div className="p-2" style={{ background: "var(--surface)", borderRadius: 12 }}><p className="at-label mb-1">Avant</p>{action.avant.map((x, i) => <p key={i} className="break-words">{x}</p>)}</div>
          <div className="p-2" style={{ background: "color-mix(in srgb, var(--success) 12%, transparent)", borderRadius: 12 }}><p className="at-label mb-1">Après</p>{action.apres.map((x, i) => <p key={i} className="break-words" style={{ fontWeight: action.avant.includes(x) ? 400 : 700 }}>{x}</p>)}</div>
        </div>
      )}
      {edition && !traitee && (
        <div ref={formRef} className="space-y-2" data-testid="ai-action-edit-form">
          {action.champs.map((c) => (
            <label key={c.cle} className="block">
              <span className="at-label block mb-1">{c.label}</span>
              <ChampAction c={c} valeur={params[c.cle]} onChange={(v) => setParams({ ...params, [c.cle]: v })} testId={`ai-action-field-${c.cle}`} />
            </label>
          ))}
        </div>
      )}
      {!traitee && (
        <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-2">
          <button type="button" onClick={valider} disabled={envoi} className="at-btn at-btn-primary min-w-0" data-testid="ai-action-validate"><Check size={16} />Valider</button>
          <button type="button" onClick={() => setEdition(!edition)} className="at-btn at-btn-ghost min-w-0" data-testid="ai-action-modify"><Pencil size={16} />{edition ? "Fermer" : "Modifier"}</button>
        </div>
      )}
      {action.statut === "fait" && action.annulable && !action.annule && (
        <button type="button" onClick={annuler} className="at-btn at-btn-ghost w-full" data-testid="ai-action-undo"><Undo2 size={16} />Annuler</button>
      )}
    </div>
  );
};
